import { useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useIsEditAllowedWithOperator } from '../../utils/utils';
import { getErrorMessage } from '../../utils/errorHandler';
import useDebounce from '../../utils/debounce';
import WarningMessage from '../../components/WarningMessage';
import {
  useCreateDesignation,
  useDeleteDesignation,
  useFetchDesignations,
  useUpdateDesignation,
} from './Hooks';

const { Title } = Typography;

export default function DesignationsPage() {
  const { t } = useTranslation('designation');
  const { user } = useAuthStore();
  const { operatorId } = useOperatorStore();
  const isEditAllowed = useIsEditAllowedWithOperator();
  const isReadOnly = user?.role === 'READ_ONLY_ADMIN';
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form] = Form.useForm();

  const { data, isLoading, isFetching, refetch } = useFetchDesignations({
    operatorId,
    page,
    search: debouncedSearch,
    limit: pageSize,
  });

  const { mutate: createDesignation, isPending: creating } = useCreateDesignation({
    onSuccess: () => {
      message.success(t('createSuccess'));
      closeDrawer();
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const { mutate: updateDesignation, isPending: updating } = useUpdateDesignation({
    onSuccess: () => {
      message.success(t('updateSuccess'));
      closeDrawer();
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const { mutate: deleteDesignation, isPending: deleting } = useDeleteDesignation({
    onSuccess: () => {
      message.success(t('deleteSuccess'));
      setDeleteTarget(null);
    },
    onError: (error) => message.error(getErrorMessage(error, t('actionFailed'))),
  });

  const designations = data?.paginatedData || [];
  const totalCount = data?.totalCount || 0;

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ name: record.name || '' });
    setDrawerOpen(true);
  };

  const onFinish = (values) => {
    const payload = {
      operatorId,
      name: values.name?.trim().replace(/\s+/g, ' '),
    };
    if (editing) {
      updateDesignation({ id: editing._id, ...payload });
      return;
    }
    createDesignation(payload);
  };

  const columns = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      width: 140,
      render: (_, record) =>
        isEditAllowed && !isReadOnly ? (
          <div className="flex justify-center gap-2">
            <Tooltip title={t('edit')}>
              <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
            </Tooltip>
            <Tooltip title={t('delete')}>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => setDeleteTarget(record)}
              />
            </Tooltip>
          </div>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div className="space-y-4">
      {!operatorId ? (
        <WarningMessage />
      ) : (
        <>
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Input
                allowClear
                placeholder={t('searchPlaceholder')}
                prefix={<SearchOutlined />}
                style={{ width: 300 }}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (page !== 1) setPage(1);
                }}
              />
              <Tooltip title={t('refresh')}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => refetch()}
                  loading={isFetching}
                />
              </Tooltip>
            </div>
            {isEditAllowed && !isReadOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {t('add')}
              </Button>
            )}
          </div>
          <Table
            columns={columns}
            dataSource={designations}
            loading={isLoading || isFetching}
            rowKey={(record) => record._id}
            size="small"
            pagination={{
              current: page,
              pageSize,
              total: totalCount,
              showSizeChanger: false,
              onChange: setPage,
              position: ['bottomCenter'],
            }}
          />
        </>
      )}

      <Drawer
        title={
          <Title level={4} className="!mb-0">
            {editing ? t('edit') : t('createTitle')}
          </Title>
        }
        open={drawerOpen}
        onClose={closeDrawer}
        width={420}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="name"
            label={t('name')}
            rules={[
              { required: true, message: t('nameRequired') },
              { max: 80, message: t('nameMax') },
            ]}
          >
            <Input placeholder={t('enterName')} />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={closeDrawer}>{t('cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={creating || updating}>
              {t('save')}
            </Button>
          </div>
        </Form>
      </Drawer>

      <Modal
        title={<Title level={4} className="!mb-0">{t('deleteTitle')}</Title>}
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={() => deleteDesignation({ id: deleteTarget?._id, operatorId })}
        okText={t('delete')}
        cancelText={t('cancel')}
        okButtonProps={{ danger: true, loading: deleting }}
      >
        <p>{t('deleteConfirm')}</p>
        {deleteTarget?.name ? <p><strong>{deleteTarget.name}</strong></p> : null}
      </Modal>
    </div>
  );
}
